import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import MetaTags from 'react-meta-tags';
import axios from '../api/axios';
import moment from 'moment';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import jwtDecode from 'jwt-decode'; // Correct import for jwt-decode
import FeatherIcon from 'feather-icons-react';
import { getPropertyList } from '../Inventory/utils/api';
import { getOrders, getMROSuppliers, getInventoryItems, createOrder, updateOrder } from './utils/orderApi';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import '../../assets/css/rentdigicare.css';

// MIUI-inspired styles
const miuiStyles = `
                                                .miui-card {
                                                  border-radius: 16px;
                                                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                                                  background: #fff;
                                                  margin-bottom: 20px;
                                                }
                                                .miui-button {
                                                  border-radius: 20px;
                                                  padding: 8px 24px;
                                                  font-weight: 500;
                                                  text-transform: none;
                                                  transition: all 0.3s ease;
                                                }
                                                .miui-dialog {
                                                  .MuiDialog-paper {
                                                    border-radius: 16px;
                                                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                                                    background: #fff;
                                                    min-width: 600px;
                                                  }
                                                  .MuiDialogTitle-root {
                                                    border-bottom: none;
                                                    padding: 20px 24px;
                                                    background: #f8f9fa;
                                                    border-radius: 16px 16px 0 0;
                                                  }
                                                  .MuiDialogActions-root {
                                                    border-top: none;
                                                    padding: 16px 24px;
                                                  }
                                                  .MuiDialogContent-root {
                                                    padding: 20px 24px;
                                                  }
                                                }
                                                .miui-form-field {
                                                  margin-bottom: 16px;
                                                }
                                                .miui-table {
                                                  border-radius: 12px;
                                                  overflow: hidden;
                                                  background: #fff;
                                                  .table {
                                                    margin-bottom: 0;
                                                  }
                                                  .table thead th {
                                                    background: #f8f9fa;
                                                    border-bottom: none;
                                                    padding: 16px;
                                                    font-weight: 500;
                                                    color: #333;
                                                  }
                                                  .table td {
                                                    padding: 16px;
                                                    vertical-align: middle;
                                                  }
                                                }
                                                .scrollable-table {
                                                  max-height: 600px;
                                                  overflow-y: auto;
                                                  position: relative;
                                                }
                                                .loader-container {
                                                  display: flex;
                                                  justify-content: center;
                                                  align-items: center;
                                                  min-height: 200px;
                                                }
                                              `;

// Validation schema for the Add Order form
const validationSchema = Yup.object().shape({
  ordered_items: Yup.array()
    .of(
      Yup.object().shape({
        inventory_item_id: Yup.string().required('Item is required'),
        description: Yup.string(),
        requested_quantity: Yup.number()
          .required('Quantity is required')
          .min(1, 'Quantity must be at least 1')
          .typeError('Quantity must be a number'),
      })
    )
    .min(1, 'At least one item is required'),
  supplier_id: Yup.string().required('Supplier is required'),
  notes: Yup.string(),
  is_reorder: Yup.boolean(),
});

const OrderList = () => {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [buildingList, setBuildingList] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [orderLists, setOrderLists] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [updateId, setUpdateId] = useState('');
  const [approvedStatus, setApprovedStatus] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [addOrderModal, setAddOrderModal] = useState(false);
  const [noBuildingsModalOpen, setNoBuildingsModalOpen] = useState(false);

  // Decode JWT token with error handling
  let decode = null;
  try {
    const token = window.localStorage.getItem('accessToken');
    if (token) {
      decode = jwtDecode(token);
    } else {
      console.error('No access token found');
      toast.error('Please log in to continue');
    }
  } catch (error) {
    console.error('Error decoding JWT:', error);
    toast.error('Invalid session. Please log in again.');
  }

  // Inject MIUI styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = miuiStyles;
    document.head.appendChild(styleSheet);
    getPropertyListData();
    getVendorsList();
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Fetch property list
  const getPropertyListData = async () => {
    try {
      setLoading(true);
      const result = await getPropertyList(decode?.role || '', decode?.id || '');
      const properties = result?.propertyList || [];
      setProperties(properties);
      if (properties.length === 0) {
        setNoBuildingsModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to fetch properties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle property selection
  const handlePropertyChange = (event) => {
    const propertyId = event.target.value;
    const selected = properties.find((prop) => prop._id === propertyId);
    if (selected) {
      const propertyData = {
        id: selected._id,
        value: selected.title,
        label: `${selected.title}, ${selected.location}`,
        building: selected.building || [],
      };
      setSelectedProperty(propertyData);
      const buildings = selected.building
        .map((b) => b.buildingName)
        .filter((name) => name);
      setBuildingList(buildings);
      setSelectedBuilding('');
      setOrderLists([]);
      setInventoryItems([]);
      if (buildings.length === 0) {
        setNoBuildingsModalOpen(true);
      } else {
        setNoBuildingsModalOpen(false);
      }
    }
  };

  // Handle building selection
  const handleBuildingChange = (event) => {
    const buildingName = event.target.value;
    setSelectedBuilding(buildingName);
    if (buildingName) {
      handleGetInventoryOrders(selectedProperty.id, buildingName);
      handleGetInventoryItems(selectedProperty.id, buildingName);
    } else {
      setOrderLists([]);
      setInventoryItems([]);
    }
  };

  // Fetch MRO suppliers
  const getVendorsList = async () => {
    try {
      const response = await getMROSuppliers();
      if (response.success && response.data.length) {
        setVendorList(response.data);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      toast.error('Failed to fetch suppliers. Please try again.');
    }
  };

  // Fetch inventory items
  const handleGetInventoryItems = async (propertyId, buildingName) => {
    try {
      setLoading(true);
      const response = await getInventoryItems(propertyId, buildingName);
      if (response.success) {
        setInventoryItems(response.items);
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      toast.error('Failed to fetch inventory items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch inventory orders
  const handleGetInventoryOrders = async (propertyId, buildingName) => {
    setLoading(true);
    try {
      const response = await getOrders(propertyId, buildingName);
      if (response.success && response.data.length) {
        const orders = response.data.map((order) => ({
          ...order,
          newOrderItems: order.ordered_items.map((item) => ({
            category: 'General', // Simplified for display
            items: [{ name: item.name, description: item.description || 'N/A', itemQuantity: item.requested_quantity }],
          })),
        }));
        setOrderLists(orders);
      } else {
        setOrderLists([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders. Please try again.');
      setOrderLists([]);
    } finally {
      setLoading(false);
    }
  };

  // Update order (approve/reject)
  const handleUpdateData = async () => {
    try {
      setLoading(true);
      const updateData = {
        status: approvedStatus ? 'approved' : 'rejected',
        supplier_id: selectedVendorId,
        approved_by: decode?.id || '',
        approved_date: approvedStatus ? new Date() : undefined,
      };
      const response = await updateOrder(updateId, updateData);
      if (response.success) {
        handleGetInventoryOrders(selectedProperty.id, selectedBuilding);
        setShowModal(false);
        toast.success('Order Updated Successfully');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Create new order
  const handleCreateOrder = async (values, { setSubmitting, resetForm }) => {
    try {
      setLoading(true);
      const payload = {
        property_id: selectedProperty.id,
        building_name: selectedBuilding,
        supplier_id: values.supplier_id,
        ordered_items: values.ordered_items.map((item) => ({
          name: inventoryItems.find((i) => i._id === item.inventory_item_id)?.name || '',
          description: item.description,
          requested_quantity: Number(item.requested_quantity),
        })),
        ordered_by: decode?.id || '',
        notes: values.notes,
        is_reorder: values.is_reorder,
        status: 'draft',
      };
      const response = await createOrder(payload);
      if (response.success) {
        toast.success('Order created successfully');
        setAddOrderModal(false);
        resetForm();
        handleGetInventoryOrders(selectedProperty.id, selectedBuilding);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.message || 'Failed to create order. Please try again.');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Table columns
  const columns = [
    {
      dataField: 'index',
      text: '#',
      formatter: (cell, row, rowIndex) => rowIndex + 1,
    },
    {
      dataField: 'newOrderItems',
      text: 'Item Category',
      formatter: (cell) => cell.map((obj) => obj.category).join(', '),
    },
    {
      dataField: 'newOrderItems',
      text: 'Item Name',
      formatter: (cell) => cell.flatMap((obj) => obj.items.map((item) => item.name)).join(', '),
    },
    {
      dataField: 'newOrderItems',
      text: 'Description',
      formatter: (cell) => cell.flatMap((obj) => obj.items.map((item) => item.description)).join(', '),
    },
    {
      dataField: 'newOrderItems',
      text: 'Order Quantity',
      formatter: (cell) => cell.flatMap((obj) => obj.items.map((item) => item.itemQuantity)).join(', '),
    },
    {
      dataField: 'order_date',
      text: 'Order Date',
      formatter: (cell) => moment(cell).format('DD/MM/YYYY'),
    },
    {
      dataField: 'ordered_by.ownerFirstName',
      text: 'Ordered By',
      formatter: (cell, row) => `${row.ordered_by?.ownerFirstName || ''} ${row.ordered_by?.ownerLastName || ''}`,
    },
    {
      dataField: 'actions',
      text: 'Actions',
      formatter: (cell, row) => (
        <Box display="flex" gap={1}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Supplier</InputLabel>
            <Select
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
              label="Supplier"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Select Supplier</em>
              </MenuItem>
              {vendorList.map((vendor) => (
                <MenuItem key={vendor._id} value={vendor._id}>
                  {vendor.first_name} {vendor.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            onClick={() => {
              setUpdateId(row._id);
              setApprovedStatus(true);
              setShowModal(true);
            }}
            title="Approve"
            disabled={loading}
          >
            <FeatherIcon icon="check-circle" size={20} color="green" />
          </IconButton>
          <IconButton
            onClick={() => {
              setUpdateId(row._id);
              setApprovedStatus(false);
              setShowModal(true);
            }}
            title="Reject"
            disabled={loading}
          >
            <FeatherIcon icon="x-circle" size={20} color="red" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const paginationOptions = {
    sizePerPage: 10,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true,
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | Order List</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Order List" />

          <div className="row">
            <div className="col-12">
              <Card className="miui-card">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography variant="h4">Order List</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      className="miui-button"
                      onClick={() => setAddOrderModal(true)}
                      disabled={loading || !selectedBuilding || inventoryItems.length === 0}
                    >
                      <FeatherIcon icon="plus-circle" className="me-2" />
                      Add Order
                    </Button>
                  </Box>

                  {/* Property and Building Selection */}
                  <Box mb={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <InputLabel>Select Property</InputLabel>
                          <Select
                            value={selectedProperty?.id || ''}
                            onChange={handlePropertyChange}
                            label="Select Property"
                            disabled={loading}
                          >
                            <MenuItem value="">
                              <em>Select a property</em>
                            </MenuItem>
                            {properties.map((prop) => (
                              <MenuItem key={prop._id} value={prop._id}>
                                {prop.title}, {prop.location}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {buildingList.length > 0 && (
                        <Grid item xs={6}>
                          <FormControl fullWidth>
                            <InputLabel>Select Building</InputLabel>
                            <Select
                              value={selectedBuilding}
                              onChange={handleBuildingChange}
                              label="Select Building"
                              disabled={loading}
                            >
                              <MenuItem value="">
                                <em>Select a building</em>
                              </MenuItem>
                              {buildingList.map((building, index) => (
                                <MenuItem key={index} value={building}>
                                  {building}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {loading ? (
                    <Box className="loader-container">
                      <CircularProgress />
                    </Box>
                  ) : orderLists.length > 0 ? (
                    <div className="miui-table scrollable-table">
                      <BootstrapTable
                        keyField="_id"
                        data={orderLists}
                        columns={columns}
                        pagination={paginationFactory(paginationOptions)}
                        bootstrap4
                        striped
                        hover
                        condensed
                      />
                    </div>
                  ) : (
                    <Typography align="center">No orders available</Typography>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Add Order Dialog */}
      <Dialog
        open={addOrderModal}
        onClose={() => setAddOrderModal(false)}
        className="miui-dialog"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Order</DialogTitle>
        <Formik
          initialValues={{
            ordered_items: [{ inventory_item_id: '', description: '', requested_quantity: 1 }],
            supplier_id: '',
            notes: '',
            is_reorder: false,
          }}
          validationSchema={validationSchema}
          onSubmit={handleCreateOrder}
        >
          {({ errors, touched, values, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Order Items
                </Typography>
                {values.ordered_items.map((item, index) => (
                  <Box key={index} className="miui-form-field" mb={2} p={2} border={1} borderRadius={2}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel>Item</InputLabel>
                          <Field
                            as={Select}
                            name={`ordered_items[${index}].inventory_item_id`}
                            label="Item"
                            error={
                              touched.ordered_items?.[index]?.inventory_item_id &&
                              !!errors.ordered_items?.[index]?.inventory_item_id
                            }
                            disabled={isSubmitting || loading}
                          >
                            <MenuItem value="">
                              <em>Select an item</em>
                            </MenuItem>
                            {inventoryItems.map((item) => (
                              <MenuItem key={item._id} value={item._id}>
                                {item.name}
                              </MenuItem>
                            ))}
                          </Field>
                          {touched.ordered_items?.[index]?.inventory_item_id &&
                            errors.ordered_items?.[index]?.inventory_item_id && (
                              <Typography color="error" variant="caption">
                                {errors.ordered_items[index].inventory_item_id}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <Field
                          as={TextField}
                          name={`ordered_items[${index}].description`}
                          label="Description"
                          fullWidth
                          error={
                            touched.ordered_items?.[index]?.description &&
                            !!errors.ordered_items?.[index]?.description
                          }
                          helperText={
                            touched.ordered_items?.[index]?.description &&
                            errors.ordered_items?.[index]?.description
                          }
                          disabled={isSubmitting || loading}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Box display="flex" alignItems="center">
                          <IconButton
                            onClick={() =>
                              setFieldValue(
                                `ordered_items[${index}].requested_quantity`,
                                Math.max(1, Number(values.ordered_items[index].requested_quantity) - 1)
                              )
                            }
                            disabled={isSubmitting || loading || values.ordered_items[index].requested_quantity <= 1}
                          >
                            <FeatherIcon icon="minus-circle" size={20} />
                          </IconButton>
                          <Field
                            as={TextField}
                            name={`ordered_items[${index}].requested_quantity`}
                            label="Quantity"
                            type="number"
                            fullWidth
                            error={
                              touched.ordered_items?.[index]?.requested_quantity &&
                              !!errors.ordered_items?.[index]?.requested_quantity
                            }
                            helperText={
                              touched.ordered_items?.[index]?.requested_quantity &&
                              errors.ordered_items?.[index]?.requested_quantity
                            }
                            disabled={isSubmitting || loading}
                            inputProps={{ min: 1 }}
                          />
                          <IconButton
                            onClick={() =>
                              setFieldValue(
                                `ordered_items[${index}].requested_quantity`,
                                Number(values.ordered_items[index].requested_quantity) + 1
                              )
                            }
                            disabled={isSubmitting || loading}
                          >
                            <FeatherIcon icon="plus-circle" size={20} />
                          </IconButton>
                        </Box>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          onClick={() => {
                            const newItems = [...values.ordered_items];
                            newItems.splice(index, 1);
                            setFieldValue('ordered_items', newItems);
                          }}
                          disabled={values.ordered_items.length === 1 || isSubmitting || loading}
                        >
                          <FeatherIcon icon="trash-2" size={20} color="red" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  className="miui-button"
                  onClick={() => {
                    setFieldValue('ordered_items', [
                      ...values.ordered_items,
                      { inventory_item_id: '', description: '', requested_quantity: 1 },
                    ]);
                  }}
                  disabled={isSubmitting || loading}
                >
                  Add Another Item
                </Button>

                <Box className="miui-form-field" mt={2}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Field
                      as={Select}
                      name="supplier_id"
                      label="Supplier"
                      error={touched.supplier_id && !!errors.supplier_id}
                      disabled={isSubmitting || loading}
                    >
                      <MenuItem value="">
                        <em>Select a supplier</em>
                      </MenuItem>
                      {vendorList.map((vendor) => (
                        <MenuItem key={vendor._id} value={vendor._id}>
                          {vendor.first_name} {vendor.last_name}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.supplier_id && errors.supplier_id && (
                      <Typography color="error" variant="caption">
                        {errors.supplier_id}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                <Box className="miui-form-field">
                  <Field
                    as={TextField}
                    name="notes"
                    label="Notes"
                    multiline
                    rows={4}
                    fullWidth
                    error={touched.notes && !!errors.notes}
                    helperText={touched.notes && errors.notes}
                    disabled={isSubmitting || loading}
                  />
                </Box>
                <Box className="miui-form-field">
                  <FormControlLabel
                    control={
                      <Field
                        as={Checkbox}
                        name="is_reorder"
                        checked={values.is_reorder}
                        disabled={isSubmitting || loading}
                      />
                    }
                    label="Is Reorder"
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  className="miui-button"
                  onClick={() => setAddOrderModal(false)}
                  disabled={isSubmitting || loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="miui-button"
                  disabled={isSubmitting || loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Order'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        className="miui-dialog"
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {approvedStatus ? 'Approve' : 'Reject'} this order?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            className="miui-button"
            onClick={() => setShowModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={approvedStatus ? 'success' : 'error'}
            className="miui-button"
            onClick={handleUpdateData}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* No Buildings Alert Dialog */}
      <Dialog
        open={noBuildingsModalOpen}
        onClose={() => setNoBuildingsModalOpen(false)}
        className="miui-dialog"
      >
        <DialogTitle>No Buildings Available</DialogTitle>
        <DialogContent>
          <Alert severity="error">No buildings are available for the selected property.</Alert>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            className="miui-button"
            onClick={() => setNoBuildingsModalOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </React.Fragment>
  );
};

export default OrderList;